import * as EmailValidator from 'email-validator';

import {
  DistributionRowValidation,
  DistributionTableRow,
} from '../models/distribution/distribution.model';

export const DISTRIBUTION_MANDATORY_MESSAGES = {
  phone: 'The format should follow +<Dial Code> <Phone Number>',
  phoneBlank: 'Phone is required',
  firstName: 'First name is required',
  lastName: 'Last name is required',
  name: 'First name and Last name is required',
  email: 'Email address must be valid',
  emailBlank: 'Email is required',
  order: 'The format should follow <C><priority number> or <E><priority number>',
  orderBlank: 'Order is required',
} as const;

export const DISTRIBUTION_OPTIONAL_MESSAGES = {
  name: 'First name and Last name are recommended',
  firstName: 'First name is recommended',
  lastName: 'Last name is recommended',
  title: 'Title is recommended',
  phone: 'The format should follow +<Dial Code> <Phone Number>',
  phoneBlank: 'phone is recommended',
} as const;

const PHONE_REGEX = /^\+(1|[1-9][0-9]{0,2}) [1-9][0-9]{5,11}$/;
const CONSECUTIVE_HYPHEN_REGEX = /--+/;
const ORDER_REGEX = /^[EC]\d+/;
const BLANK_PLACEHOLDERS = ['n/a', 'no data'];
export const DISTRIBUTION_BLANK_DISPLAY = 'N/A';

/** Raw values of one distribution contact, before validation. */
export interface DistributionContactInput {
  firstName: string;
  lastName: string;
  halId: string;
  title: string;
  email: string;
  phone: string;
  order: string;
  /** Text shown in the name hover when the name is valid. */
  fullName: string;
}

/** Validation result and the row flags/hovers derived from it. */
export interface DistributionContactChecks {
  validation: DistributionRowValidation;
  namesBothBlank: boolean;
  titleBlank: boolean;
  emailBlank: boolean;
  phoneBlank: boolean;
  orderBlank: boolean;
  nameHover: string;
  phoneHover: string;
  emailHover: string;
  orderHover: string;
}

export interface DistributionTabStatus {
  isError: boolean;
  isWarning: boolean;
}

/** Converts `#RRGGBB` to `rgba(r, g, b, alpha)`; other values are returned as is. */
export function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  if (value.length !== 6) {
    return hex;
  }
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function createEmptyDistributionValidation(): DistributionRowValidation {
  return {
    mandatory: {
      email: false,
      phone: false,
      order: false,
      firstName: false,
      lastName: false,
    },
    optional: {
      phone: false,
      firstName: false,
      lastName: false,
      halId: false,
      title: false,
    },
  };
}

/** Trims a value and treats `N/A` / `No data` placeholders as blank. */
export function cleanDistributionValue(value: unknown): string {
  const text = String(value ?? '').trim();
  return BLANK_PLACEHOLDERS.includes(text.toLowerCase()) ? '' : text;
}

export function isValidDistributionPhone(rawPhone: string): boolean {
  const cleaned = rawPhone.replace(/-/g, '').trim();
  return (
    !!cleaned &&
    !CONSECUTIVE_HYPHEN_REGEX.test(rawPhone) &&
    PHONE_REGEX.test(cleaned)
  );
}

function getEmailHover(email: string, isInvalid: boolean): string {
  if (!isInvalid) {
    return email;
  }
  return email
    ? DISTRIBUTION_MANDATORY_MESSAGES.email
    : DISTRIBUTION_MANDATORY_MESSAGES.emailBlank;
}

function getPhoneHover(
  rawPhone: string,
  validation: DistributionRowValidation,
  isPrimary: boolean
): string {
  if (isPrimary) {
    if (!validation.mandatory.phone) {
      return rawPhone;
    }
    return rawPhone
      ? DISTRIBUTION_MANDATORY_MESSAGES.phone
      : DISTRIBUTION_MANDATORY_MESSAGES.phoneBlank;
  }
  return validation.optional.phone
    ? DISTRIBUTION_OPTIONAL_MESSAGES.phone
    : rawPhone;
}

/**
 * Validates one distribution contact. `isPrimary` is true for the
 * "Primary Points Of Contact" tab, where phone, order and names are mandatory.
 */
export function validateDistributionContact(
  input: DistributionContactInput,
  isPrimary: boolean
): DistributionContactChecks {
  const { firstName, lastName, halId, title, email, phone, order, fullName } =
    input;
  const validation = createEmptyDistributionValidation();
  const namesBothBlank = !firstName && !lastName;

  // Email: mandatory on every tab.
  validation.mandatory.email = !email || !EmailValidator.validate(email);

  // Phone: mandatory on the primary tab, a soft warning elsewhere.
  const isValidPhone = isValidDistributionPhone(phone);
  if (isPrimary) {
    validation.mandatory.phone = !phone || !isValidPhone;
  } else {
    validation.optional.phone = !!phone && !isValidPhone;
  }

  // Order: primary tab only.
  validation.mandatory.order = isPrimary && (!order || !ORDER_REGEX.test(order));

  // First / last name.
  validation.mandatory.firstName = isPrimary && !firstName;
  validation.mandatory.lastName = isPrimary && !lastName;
  validation.optional.firstName = !firstName;
  validation.optional.lastName = !lastName;
  validation.optional.halId = !halId;
  validation.optional.title = !title;

  let nameHover = fullName;
  if (isPrimary) {
    if (validation.mandatory.firstName) {
      nameHover = DISTRIBUTION_MANDATORY_MESSAGES.firstName;
    } else if (validation.mandatory.lastName) {
      nameHover = DISTRIBUTION_MANDATORY_MESSAGES.lastName;
    }
  } else if (validation.optional.firstName) {
    nameHover = DISTRIBUTION_OPTIONAL_MESSAGES.firstName;
  } else if (validation.optional.lastName) {
    nameHover = DISTRIBUTION_OPTIONAL_MESSAGES.lastName;
  }

  let orderHover = order;
  if (validation.mandatory.order) {
    orderHover = order
      ? DISTRIBUTION_MANDATORY_MESSAGES.order
      : DISTRIBUTION_MANDATORY_MESSAGES.orderBlank;
  }

  return {
    validation,
    namesBothBlank,
    titleBlank: !title,
    emailBlank: !email,
    phoneBlank: !phone,
    orderBlank: !order,
    nameHover,
    phoneHover: getPhoneHover(phone, validation, isPrimary),
    emailHover: getEmailHover(email, validation.mandatory.email),
    orderHover,
  };
}

/**
 * Recomputes the email, phone, title and HAL ID checks of an existing row after it was added or
 * edited. `hasHalId` decides the HAL ID warning; when omitted it falls back to `company`.
 * The name and order checks are kept because those values cannot be edited on the page.
 */
export function revalidateDistributionRow<T extends DistributionTableRow>(
  row: T,
  isPrimary: boolean,
  hasHalId?: boolean
): T & DistributionTableRow {
  const email = cleanDistributionValue(row.email);
  const phone = cleanDistributionValue(row.phone);
  const title = cleanDistributionValue(row.title);

  const previous = row.validation;
  const validation: DistributionRowValidation = {
    mandatory: {
      ...createEmptyDistributionValidation().mandatory,
      ...previous?.mandatory,
    },
    optional: {
      ...createEmptyDistributionValidation().optional,
      ...previous?.optional,
    },
  };

  validation.mandatory.email = !email || !EmailValidator.validate(email);
  const isValidPhone = isValidDistributionPhone(phone);
  validation.mandatory.phone = isPrimary && (!phone || !isValidPhone);
  validation.optional.phone = !isPrimary && !!phone && !isValidPhone;
  validation.optional.title = !title;
  validation.optional.halId = hasHalId === undefined ? row.company !== 'Halliburton' : !hasHalId;

  return {
    ...row,
    email: email || DISTRIBUTION_BLANK_DISPLAY,
    phone: phone || DISTRIBUTION_BLANK_DISPLAY,
    title: title || DISTRIBUTION_BLANK_DISPLAY,
    emailBlank: !email,
    phoneBlank: !phone,
    titleBlank: !title,
    emailHover: getEmailHover(email, validation.mandatory.email),
    phoneHover: getPhoneHover(phone, validation, isPrimary),
    validation,
  };
}

/** Error / warning state of a single row on a tab. */
export function getDistributionRowStatus(
  row: DistributionTableRow,
  isPrimary: boolean
): DistributionTabStatus {
  const mandatory = row.validation?.mandatory;
  const optional = row.validation?.optional;

  const isError =
    (!!mandatory && Object.values(mandatory).some(Boolean)) ||
    !!row.emailBlank ||
    (isPrimary && (!!row.namesBothBlank || !!row.phoneBlank));

  const isWarning =
    (!!optional && Object.values(optional).some(Boolean)) ||
    !!row.titleBlank ||
    (!isPrimary && !!row.namesBothBlank);

  return { isError, isWarning };
}

/**
 * Error / warning state of a tab. `isWarning` is independent of `isError`;
 * the UI shows the warning icon only when the tab has no error.
 */
export function getDistributionTabStatus(
  rows: DistributionTableRow[],
  isPrimary: boolean
): DistributionTabStatus {
  let isError = false;
  let isWarning = false;
  for (const row of rows) {
    const status = getDistributionRowStatus(row, isPrimary);
    isError = isError || status.isError;
    isWarning = isWarning || status.isWarning;
  }
  return { isError, isWarning };
}
